import { useState } from "react";
import { Play, X, Expand } from "lucide-react";
import { reels, stills } from "@/lib/data";
import { useVimeoPosters } from "@/hooks/useVimeoPosters";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";

export default function ReelSection() {
  const [active, setActive] = useState(null);
  const [activeStill, setActiveStill] = useState(null);
  const posters = useVimeoPosters(reels);

  return (
    <section
      id="reel"
      data-testid="section-reel"
      className="relative bg-[#050505] py-24 md:py-36 px-6 md:px-12 lg:px-24"
    >
      {/* Section header */}
      <div className="mx-auto max-w-[1600px]">
        <div className="grid grid-cols-12 gap-8 items-end mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-2">
            <p className="font-accent text-[10px] text-[#D4AF37]">Part 01</p>
          </div>
          <div className="col-span-12 md:col-span-7">
            <h2 className="font-serif-display text-5xl md:text-7xl tracking-tighter text-white leading-[1]">
              The Reel.
            </h2>
            <p className="mt-6 text-zinc-500 max-w-md font-light">
              The finished frames &mdash; commercials, music videos and short
              films delivered between 2022 and now.
            </p>
          </div>
          <div className="col-span-12 md:col-span-3 md:text-right">
            <p className="font-accent text-[10px] text-zinc-600">
              {reels.length.toString().padStart(2, "0")} Selected Works
            </p>
          </div>
        </div>

        {/* Reel grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
          {reels.map((r, i) => (
            <button
              key={r.id}
              data-testid={`reel-card-${r.id}`}
              onClick={() => setActive(r)}
              className={`group relative text-left ${
                i % 3 === 1 ? "md:translate-y-16" : ""
              }`}
            >
              <div className="relative overflow-hidden bg-[#0A0A0A] aspect-[2.35/1]">
                <img
                  src={posters[r.id] || r.poster}
                  alt={r.title}
                  className="absolute inset-0 w-full h-full object-cover transition-all duration-[1200ms] ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div
                    data-testid={`reel-play-button-${r.id}`}
                    className="w-16 h-16 md:w-20 md:h-20 rounded-full border border-white/60 flex items-center justify-center backdrop-blur-sm bg-black/20 group-hover:bg-[#D4AF37] group-hover:border-[#D4AF37] transition-all duration-500"
                  >
                    <Play
                      size={22}
                      strokeWidth={1}
                      className="text-white group-hover:text-black ml-1 transition-colors"
                      fill="currentColor"
                    />
                  </div>
                </div>
                <div className="absolute top-4 left-4 flex items-center gap-3">
                  <span className="font-accent text-[9px] text-white/80 bg-black/40 backdrop-blur-sm px-3 py-1">
                    {r.format}
                  </span>
                  <span className="font-accent text-[9px] text-white/60">
                    {r.runtime}
                  </span>
                </div>
                <div className="absolute bottom-4 right-4">
                  <span className="font-accent text-[9px] text-white/50">
                    {String(i + 1).padStart(2, "0")} / {String(reels.length).padStart(2, "0")}
                  </span>
                </div>
              </div>

              <div className="mt-5 flex items-start justify-between gap-6">
                <div>
                  <h3 className="font-serif-display text-2xl md:text-3xl text-white group-hover:text-[#D4AF37] transition-colors">
                    {r.title}
                  </h3>
                  <p className="mt-1 text-sm text-zinc-500 font-light">
                    {r.role} &middot; {r.year}
                  </p>
                </div>
                <span className="font-accent text-[10px] text-zinc-600 group-hover:text-[#D4AF37] transition-colors mt-2">
                  Play →
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Selected Stills sub-section */}
      <div className="mx-auto max-w-[1600px] mt-32 md:mt-44">
        <div className="grid grid-cols-12 gap-8 items-end mb-12 md:mb-16">
          <div className="col-span-12 md:col-span-2">
            <p className="font-accent text-[10px] text-[#D4AF37]">Part 01 &middot; ii</p>
          </div>
          <div className="col-span-12 md:col-span-7">
            <h3 className="font-serif-display text-3xl md:text-5xl tracking-tighter text-white leading-[1]">
              Selected Stills.
            </h3>
            <p className="mt-4 text-zinc-500 max-w-md font-light text-sm md:text-base">
              Frames pulled straight from the timeline &mdash; no re-touch, no
              punch-in. Click any to enlarge.
            </p>
          </div>
          <div className="col-span-12 md:col-span-3 md:text-right">
            <p className="font-accent text-[10px] text-zinc-600">
              {stills.length.toString().padStart(2, "0")} Frames
            </p>
          </div>
        </div>

        {/* Stills grid: 1 hero + 2 col + 2 col */}
        <div className="grid grid-cols-12 gap-4 md:gap-6">
          {stills.map((s, i) => {
            const span =
              i === 0
                ? "col-span-12 md:col-span-8 aspect-[21/9]"
                : i === 1
                ? "col-span-12 md:col-span-4 aspect-[4/3] md:aspect-auto"
                : "col-span-6 md:col-span-3 aspect-[16/9]";
            return (
              <button
                key={s.id}
                data-testid={`still-card-${s.id}`}
                onClick={() => setActiveStill(s)}
                className={`group relative overflow-hidden bg-[#0A0A0A] ${span}`}
              >
                <img
                  src={s.src}
                  alt={s.caption}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-70 group-hover:opacity-40 transition-opacity duration-500" />
                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-9 h-9 flex items-center justify-center bg-black/60 backdrop-blur-sm border border-white/20">
                    <Expand size={14} strokeWidth={1.5} className="text-white" />
                  </div>
                </div>
                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                  <span className="font-accent text-[9px] text-white/60">
                    {String(i + 1).padStart(3, "0")}
                  </span>
                  <span className="font-accent text-[9px] text-white/80 text-right max-w-[75%] truncate">
                    {s.caption}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          data-testid="reel-lightbox"
          className="max-w-5xl w-[95vw] bg-[#050505] border border-white/10 p-0 rounded-none"
        >
          <DialogTitle className="sr-only">{active?.title || "Reel"}</DialogTitle>
          {active && (
            <div className="relative">
              <DialogClose
                data-testid="close-reel-lightbox"
                aria-label="Close"
                className="absolute top-3 right-3 z-50 w-10 h-10 flex items-center justify-center bg-black/70 hover:bg-[#D4AF37] hover:text-black text-white border border-white/15 transition-colors"
              >
                <X size={18} strokeWidth={1.5} />
              </DialogClose>
              <div className="aspect-video bg-black">
                <iframe
                  data-testid="reel-iframe"
                  src={`${active.embed}?autoplay=1&title=0&byline=0&portrait=0`}
                  title={active.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full border-0"
                />
              </div>
              <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10">
                <div className="md:col-span-2">
                  <p className="font-accent text-[10px] text-[#D4AF37]">
                    {active.format} &middot; {active.year}
                  </p>
                  <h3 className="mt-2 font-serif-display text-3xl md:text-4xl text-white">
                    {active.title}
                  </h3>
                  <p className="mt-4 text-zinc-400 font-light leading-relaxed">
                    {active.summary}
                  </p>
                </div>
                <div className="md:text-right">
                  <p className="font-accent text-[10px] text-zinc-500">Role</p>
                  <p className="font-light text-zinc-300 mt-1">{active.role}</p>
                  <p className="font-accent text-[10px] text-zinc-500 mt-6">Runtime</p>
                  <p className="font-light text-zinc-300 mt-1">{active.runtime}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Stills Lightbox */}
      <Dialog open={!!activeStill} onOpenChange={(o) => !o && setActiveStill(null)}>
        <DialogContent
          data-testid="still-lightbox"
          className="max-w-6xl w-[95vw] bg-[#050505] border border-white/10 p-0 rounded-none"
        >
          <DialogTitle className="sr-only">{activeStill?.caption || "Still"}</DialogTitle>
          {activeStill && (
            <div className="relative">
              <DialogClose
                data-testid="close-still-lightbox"
                aria-label="Close"
                className="absolute top-3 right-3 z-50 w-10 h-10 flex items-center justify-center bg-black/70 hover:bg-[#D4AF37] hover:text-black text-white border border-white/15 transition-colors"
              >
                <X size={18} strokeWidth={1.5} />
              </DialogClose>
              <img
                src={activeStill.src}
                alt={activeStill.caption}
                className="w-full h-auto max-h-[85vh] object-contain bg-black"
              />
              <div className="px-6 py-4 flex items-center justify-between border-t border-white/10">
                <span className="font-accent text-[10px] text-[#D4AF37]">
                  Film Still
                </span>
                <span className="font-light text-zinc-300 text-sm">
                  {activeStill.caption}
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
